import { useState } from 'react';
import { useDrone } from '@/lib/hooks/use-drone';
import { resumeAudioContext } from '@/lib/audio/audio-context';
import type { DroneKey, DroneInterval, DroneOctave } from '@/lib/practice/drone-keys';

// Drone defaults reset each session (no persistence — see drone-redesign Q6=A).
const DEFAULT_DRONE_ROOT: DroneKey = 'D';
const DEFAULT_DRONE_INTERVAL: DroneInterval = 'fifth';
const DEFAULT_DRONE_OCTAVE: DroneOctave = 'mid';
const DEFAULT_DRONE_VOLUME = 0.55;

export type DroneState = {
  root: DroneKey | null;
  interval: DroneInterval;
  octave: DroneOctave;
  volume: number;
  on: boolean;
  setRoot: (root: DroneKey) => void;
  setInterval: (interval: DroneInterval) => void;
  setOctave: (octave: DroneOctave) => void;
  setVolume: (volume: number) => void;
  setOn: (on: boolean) => void;
};

// Lifted drone state shared by every practice surface (desktop sidebar, tablet
// strip, mobile bottom-sheet). Owns the useDrone wiring so each surface renders
// the same control against one source of truth.
export function useDroneState(): DroneState {
  const [root, setRoot] = useState<DroneKey | null>(DEFAULT_DRONE_ROOT);
  const [interval, setInterval] = useState<DroneInterval>(DEFAULT_DRONE_INTERVAL);
  const [octave, setOctave] = useState<DroneOctave>(DEFAULT_DRONE_OCTAVE);
  const [volume, setVolume] = useState<number>(DEFAULT_DRONE_VOLUME);
  const [on, setOnState] = useState<boolean>(false);

  function setOn(next: boolean): void {
    if (next) void resumeAudioContext();
    setOnState(next);
  }

  useDrone({ root: on ? root : null, interval, octave, volume, on });

  return {
    root,
    interval,
    octave,
    volume,
    on,
    setRoot,
    setInterval,
    setOctave,
    setVolume,
    setOn,
  };
}
